import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, AlertTriangle, CheckCircle, RefreshCw, Settings,
  Loader, Globe, ChevronDown, ChevronRight, ExternalLink,
  AlertCircle, Clock, Flag, Search, X, Plus, FileText, Copy
} from "lucide-react";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
// Signal precedence (Web Risk > AI verdict > deterministic factors) lives in
// utils/brandThreatPrecedence.js so tiering, severity, sorting, and takedown
// gating can never drift apart again — and so it's unit-testable.
import { threatTier, effectiveRiskLevel, isAiCleared, isTakedownAllowed } from "../utils/brandThreatPrecedence";

// ── Constants ─────────────────────────────────────────────────────────────────

const RISK_STYLE = {
  Critical: { badge: "bg-red-100 text-red-700 border border-red-200",    dot: "bg-red-500" },
  High:     { badge: "bg-orange-50 text-orange-700 border border-orange-200", dot: "bg-orange-500" },
  Medium:   { badge: "bg-yellow-50 text-yellow-700 border border-yellow-200", dot: "bg-yellow-500" },
  Low:      { badge: "bg-green-50 text-green-700 border border-green-200",  dot: "bg-green-500" },
};

const STATUS_STYLE = {
  New:               "bg-blue-50 text-blue-700 border border-blue-200",
  UnderReview:       "bg-purple-50 text-purple-700 border border-purple-200",
  TakedownRequested: "bg-orange-50 text-orange-700 border border-orange-200",
  Resolved:          "bg-green-50 text-green-700 border border-green-200",
  FalsePositive:     "bg-gray-100 text-gray-600 border border-gray-200",
};

const STATUS_LABEL = {
  New:               "New",
  UnderReview:       "Under Review",
  TakedownRequested: "TD Requested",
  Resolved:          "Resolved",
  FalsePositive:     "False Positive",
};

const THREAT_TYPE_STYLE = {
  CertStream: "bg-indigo-50 text-indigo-700 border border-indigo-200",
  DNSTwist:   "bg-teal-50 text-teal-700 border border-teal-200",
  PhishFeed:  "bg-red-50 text-red-700 border border-red-200",
  FakeApp:    "bg-red-100 text-red-800 border border-red-300",
  UrlScan:    "bg-amber-50 text-amber-700 border border-amber-200",
  Manual:     "bg-gray-100 text-gray-600 border border-gray-200",
};

// Display-only label override — the underlying value ("FakeApp") stays as-is
// for filtering/API calls; only how it reads in the UI changes, since it's a
// discovery-SOURCE name, not a verdict, and should not assert "fake" up front.
const THREAT_TYPE_LABEL = { FakeApp: "App Scan" };

const TIER_STYLE = {
  Confirmed:        "bg-red-50 text-red-700 border border-red-200",
  Possible:         "bg-gray-100 text-gray-500 border border-gray-200",
  LikelyLegitimate: "bg-green-50 text-green-700 border border-green-200",
};

const TAKEDOWN_STATUS_LABEL = {
  Draft:        "Draft",
  Submitted:    "Submitted",
  Acknowledged: "Acknowledged",
  Resolved:     "Resolved",
  Escalated:    "Escalated",
};

const TAKEDOWN_STATUS_STYLE = {
  Draft:        "bg-gray-100 text-gray-600 border border-gray-200",
  Submitted:    "bg-blue-50 text-blue-700 border border-blue-200",
  Acknowledged: "bg-purple-50 text-purple-700 border border-purple-200",
  Resolved:     "bg-green-50 text-green-700 border border-green-200",
  Escalated:    "bg-red-50 text-red-700 border border-red-200",
};

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = "gray" }) {
  const colors = {
    red:    "bg-red-50 border-red-200 text-red-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-700",
    blue:   "bg-blue-50 border-blue-200 text-blue-700",
    green:  "bg-green-50 border-green-200 text-green-700",
    gray:   "bg-theme-secondary border-border text-content-primary",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="text-2xl font-bold">{value ?? "—"}</div>
      <div className="text-sm font-medium mt-0.5">{label}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

// Official-app registry (Settings) — the ground truth for the fake-app authenticity
// check. Self-contained: fetches and mutates via the official-apps API.
function OfficialAppsPanel({ apiCall, canManage }) {
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState({ packageName: "", platform: "Android", developerName: "" });
  const [busy, setBusy] = useState(false);
  const [certForm, setCertForm] = useState({}); // appId -> {certSha1, certSha256, keyType}
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { setApps(await apiCall("GET", "/brand-protection/official-apps") ?? []); }
    catch { /* keep prior */ }
  }, [apiCall]);
  useEffect(() => { load(); }, [load]);

  const addApp = async () => {
    if (!form.packageName.trim()) return;
    setBusy(true); setErr(null);
    try { await apiCall("POST", "/brand-protection/official-apps", form); setForm({ packageName: "", platform: "Android", developerName: "" }); await load(); }
    catch (e) { setErr(e?.response?.data?.message || "Failed to add app."); }
    finally { setBusy(false); }
  };
  const removeApp = async (id) => { try { await apiCall("DELETE", `/brand-protection/official-apps/${id}`); await load(); } catch { setErr("Failed to delete."); } };
  const confirmApp = async (id) => { try { await apiCall("PUT", `/brand-protection/official-apps/${id}/confirm`); await load(); } catch { setErr("Failed to confirm."); } };
  const addCert = async (id) => {
    const c = certForm[id] || {};
    if (!c.keyType || (!c.certSha1 && !c.certSha256)) { setErr("Cert needs a key type and at least one hash."); return; }
    try { await apiCall("POST", `/brand-protection/official-apps/${id}/certificates`, c); setCertForm(p => ({ ...p, [id]: {} })); await load(); }
    catch (e) { setErr(e?.response?.data?.message || "Failed to add cert."); }
  };
  const removeCert = async (id, certId) => { try { await apiCall("DELETE", `/brand-protection/official-apps/${id}/certificates/${certId}`); await load(); } catch { setErr("Failed to remove cert."); } };
  const setCf = (id, k, v) => setCertForm(p => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));

  return (
    <div className="bg-theme-secondary border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="h-5 w-5 text-orange-600" />
        <h2 className="text-base font-semibold text-content-primary">Official Mobile Apps</h2>
      </div>
      <p className="text-sm text-content-secondary mb-4">
        Your genuine apps — the ground truth for fake-app detection. An app matching your brand that
        isn't in this registry, or is signed with a different certificate, is flagged. Add the package /
        bundle id, and your signing-certificate fingerprint(s) (Play App Signing = multiple valid certs).
      </p>

      {err && <div className="mb-3 text-xs text-red-700">{err}</div>}

      {canManage && (
        <div className="flex flex-wrap gap-2 mb-4">
          <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
            className="rounded-lg border border-border bg-theme-primary text-content-primary text-sm px-2 py-2">
            <option value="Android">Android</option>
            <option value="iOS">iOS</option>
          </select>
          <input value={form.packageName} onChange={e => setForm(f => ({ ...f, packageName: e.target.value }))}
            placeholder="package / bundle id (e.g. com.abc.mobile)"
            className="flex-1 min-w-[220px] rounded-lg border border-border bg-theme-primary text-content-primary px-3 py-2 text-sm" />
          <input value={form.developerName} onChange={e => setForm(f => ({ ...f, developerName: e.target.value }))}
            placeholder="developer (optional)"
            className="flex-1 min-w-[160px] rounded-lg border border-border bg-theme-primary text-content-primary px-3 py-2 text-sm" />
          <button onClick={addApp} disabled={busy || !form.packageName.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            <Plus className="h-4 w-4" /> Add app
          </button>
        </div>
      )}

      {apps.length === 0 ? (
        <div className="text-sm text-content-tertiary italic">No official apps registered yet.</div>
      ) : (
        <div className="space-y-3">
          {apps.map(a => (
            <div key={a.id} className="border border-border rounded-lg p-3 bg-theme-primary">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <span className="font-mono text-sm text-content-primary">{a.packageName}</span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-theme-secondary border border-border">{a.platform}</span>
                  {a.developerName && <span className="ml-2 text-xs text-content-tertiary">{a.developerName}</span>}
                  {a.isConfirmed
                    ? <span className="ml-2 text-xs text-emerald-600">✓ confirmed</span>
                    : <span className="ml-2 text-xs text-amber-600">unconfirmed</span>}
                </div>
                {canManage && (
                  <div className="flex gap-2">
                    {!a.isConfirmed && <button onClick={() => confirmApp(a.id)} className="text-xs text-emerald-700 hover:underline">Confirm</button>}
                    <button onClick={() => removeApp(a.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                  </div>
                )}
              </div>

              {/* certs */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(a.certificates || []).map(c => (
                  <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-theme-secondary border border-border rounded text-xs font-mono">
                    {c.keyType}: {(c.certSha256 || c.certSha1 || "").slice(0, 12)}…
                    {canManage && <button onClick={() => removeCert(a.id, c.id)} className="hover:text-red-600"><X className="h-3 w-3" /></button>}
                  </span>
                ))}
                {(a.certificates || []).length === 0 && <span className="text-xs text-amber-600">No cert — authenticity can't be verified cryptographically</span>}
              </div>

              {canManage && a.platform === "Android" && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <select value={certForm[a.id]?.keyType || ""} onChange={e => setCf(a.id, "keyType", e.target.value)}
                    className="rounded border border-border bg-theme-primary text-xs px-2 py-1">
                    <option value="">key type…</option>
                    <option value="UploadKey">Upload key</option>
                    <option value="GoogleDistributed">Google-distributed</option>
                    <option value="Legacy">Legacy</option>
                    <option value="Regional">Regional</option>
                  </select>
                  <input value={certForm[a.id]?.certSha256 || ""} onChange={e => setCf(a.id, "certSha256", e.target.value)}
                    placeholder="SHA-256"
                    className="flex-1 min-w-[160px] rounded border border-border bg-theme-primary text-xs px-2 py-1 font-mono" />
                  <input value={certForm[a.id]?.certSha1 || ""} onChange={e => setCf(a.id, "certSha1", e.target.value)}
                    placeholder="SHA-1 (Aptoide)"
                    className="flex-1 min-w-[140px] rounded border border-border bg-theme-primary text-xs px-2 py-1 font-mono" />
                  <button onClick={() => addCert(a.id)} className="text-xs px-2 py-1 bg-orange-600 hover:bg-orange-500 text-white rounded">Add cert</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TermListEditor({ icon, title, description, placeholder, items, onChange, canManage, emptyText }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim().toLowerCase();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setInput("");
  };
  return (
    <div className="bg-theme-secondary border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h2 className="text-base font-semibold text-content-primary">{title}</h2>
      </div>
      <p className="text-sm text-content-secondary mb-4">{description}</p>

      <div className="flex gap-2 mb-3">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && add()}
          placeholder={placeholder}
          disabled={!canManage}
          className="flex-1 rounded-lg border border-border bg-theme-primary text-content-primary px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        />
        <button
          onClick={add}
          disabled={!canManage || !input.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      {items.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map(v => (
            <span key={v} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200 text-orange-800 rounded-full text-sm font-medium">
              {v}
              {canManage && (
                <button onClick={() => onChange(items.filter(x => x !== v))} className="hover:text-orange-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-sm text-content-tertiary italic">{emptyText}</div>
      )}
    </div>
  );
}

function ThreatRow({ threat, onStatusChange, onTakedown, canManage, aiVerdict, onAiValidate }) {
  const [expanded, setExpanded] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const effectiveRisk = effectiveRiskLevel(threat, aiVerdict);
  const aiCleared = isAiCleared(threat, aiVerdict);
  const takedownAllowed = isTakedownAllowed(threat, aiVerdict);
  const rs = RISK_STYLE[effectiveRisk] ?? RISK_STYLE.Medium;
  const ss = STATUS_STYLE[threat.status] ?? STATUS_STYLE.New;
  const ts = THREAT_TYPE_STYLE[threat.threatType] ?? THREAT_TYPE_STYLE.Manual;

  const statusOptions = ["New", "UnderReview", "TakedownRequested", "Resolved", "FalsePositive"];

  return (
    <div className="border border-border rounded-xl overflow-visible mb-2">
      {/* Header row */}
      <div
        className={`flex items-center gap-3 px-4 py-3 bg-theme-secondary cursor-pointer hover:bg-theme-tertiary transition-colors rounded-t-xl ${expanded ? '' : 'rounded-b-xl'}`}
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4 text-content-secondary" /> : <ChevronRight className="h-4 w-4 text-content-secondary" />}
        </div>

        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${aiCleared ? "bg-green-400" : rs.dot}`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-content-primary text-sm font-mono">{threat.domain}</span>
            <a
              href={`https://${threat.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-content-tertiary hover:text-blue-600"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${ts}`}>
              {THREAT_TYPE_LABEL[threat.threatType] || threat.threatType}
            </span>
            {(() => {
              if (aiVerdict?.verdict === "Impersonation")
                return (
                  <span title={aiVerdict.reason || "AI flagged likely impersonation"}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white animate-pulse">
                    ⚠ Likely Impersonation (AI)
                  </span>
                );
              if (aiCleared)
                return (
                  <span title={aiVerdict.reason || "AI: likely a legitimate same-name entity"}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
                    Likely Legitimate (AI)
                  </span>
                );
              const tier = threatTier(threat, aiVerdict);
              return (
                <span
                  title={tier === "Confirmed"
                    ? "Hard-evidence impersonation (logo/cert/feed)"
                    : "Name/title mention only — possible same-name collision; click a row → Validate with AI"}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${TIER_STYLE[tier]}`}
                >
                  {tier}
                </span>
              );
            })()}
            {threat.webRiskThreatType && (
              <span
                title={`Google Cloud Web Risk lists this as ${threat.webRiskThreatType}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-red-50 text-red-700 border border-red-200"
              >
                🛡 Known threat · Google
              </span>
            )}
            {threat.threatCategory === "App" && threat.appStore === "DomainApk" && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                APK on domain
              </span>
            )}
          </div>
          <div className="text-xs text-content-tertiary mt-0.5">
            Brand: <span className="font-medium text-content-secondary">{threat.brandTerm}</span>
            {threat.threatCategory === "App" && threat.incidentType && <> · {threat.incidentType}</>}
            {threat.registrar && <> · {threat.registrar}</>}
            {threat.hasWebsite && <> · <span className="text-orange-600">Active website</span></>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {aiVerdict?.verdict === "Impersonation" || aiCleared ? (
            <span
              title="AI assessment confidence"
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                aiVerdict.verdict === "Impersonation"
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-green-100 text-green-700 border-green-200"}`}
            >
              {typeof aiVerdict.confidence === "number" ? `${aiVerdict.confidence}% ` : ""}AI
            </span>
          ) : threat.confidenceScore > 0 ? (
            <span
              title="Detection confidence (0–100), from the evidence listed in the expanded row"
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-theme-tertiary text-content-secondary border border-border"
            >
              {threat.confidenceScore}% conf
            </span>
          ) : null}
          <span
            title={effectiveRisk !== threat.riskLevel ? `Downgraded from ${threat.riskLevel} — AI confirmed this is a legitimate different entity` : undefined}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${rs.badge}`}>
            {effectiveRisk}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ss}`}>
            {STATUS_LABEL[threat.status] ?? threat.status}
          </span>
          {/* Discovered-at timestamp hidden per product request
          <span className="text-xs text-content-tertiary hidden sm:block">{timeAgo(threat.discoveredAt)}</span>
          */}
          {canManage && takedownAllowed && threat.status !== "Resolved" && threat.status !== "FalsePositive" && (
            <button
              onClick={e => { e.stopPropagation(); onTakedown(threat); }}
              className="inline-flex items-center gap-1 px-2 py-1 bg-orange-600 hover:bg-orange-500 text-white rounded text-xs font-medium transition-colors"
            >
              <Flag className="h-3 w-3" />
              Takedown
            </button>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 py-4 bg-theme-primary border-t border-border rounded-b-xl">
          {/* GPT-5.2 impersonation adjudication — the judgment deterministic rules can't make */}
          <div className={`mb-4 p-3 rounded-lg border-l-4 border ${
            aiVerdict?.verdict === "Impersonation" ? "border-l-red-500 border-red-200 bg-red-50"
              : aiVerdict?.verdict === "LegitimateDifferentEntity" ? "border-l-green-500 border-green-200 bg-green-50"
                : "border-l-orange-400 border-border bg-theme-secondary"}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm font-semibold text-content-primary flex items-center gap-1.5">
                <span className="text-orange-500">✦</span> AI assessment <span className="font-normal text-content-tertiary">(advisory, not final)</span>
              </div>
              {canManage && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAiValidate?.(threat, !!aiVerdict?.verdict); }}
                  disabled={aiVerdict?.loading}
                  className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  {aiVerdict?.loading ? "Analyzing…" : aiVerdict?.verdict ? "Re-validate with AI" : "Validate with AI"}
                </button>
              )}
            </div>
            {/* A failed/rate-limited call is NOT a verdict — render it as an error,
                never in the verdict badge, so "couldn't run" is distinguishable
                from "ran and was uncertain". */}
            {aiVerdict?.error && !aiVerdict.loading && (
              <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{aiVerdict.error}</span>
              </div>
            )}
            {aiVerdict?.verdict && !aiVerdict.loading ? (
              <div className="mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                  aiVerdict.verdict === "Impersonation" ? "bg-red-100 text-red-700 border border-red-200"
                    : aiVerdict.verdict === "LegitimateDifferentEntity" ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-gray-100 text-gray-600 border border-gray-200"}`}>
                  {aiVerdict.verdict === "LegitimateDifferentEntity" ? "Likely legitimate (different entity)"
                    : aiVerdict.verdict === "Impersonation" ? "Likely impersonation"
                      : aiVerdict.verdict}
                  {typeof aiVerdict.confidence === "number" ? ` · ${aiVerdict.confidence}%` : ""}
                </span>
                {aiVerdict.reason && <p className="text-xs text-content-secondary mt-1">{aiVerdict.reason}</p>}
                {aiVerdict.verdict === "LegitimateDifferentEntity" && threat.webRiskThreatType && (
                  <p className="text-xs text-red-700 font-medium mt-1">
                    Overridden — Google Web Risk independently lists this URL as {threat.webRiskThreatType}.
                    That hard external signal wins: severity and tier remain escalated.
                  </p>
                )}
              </div>
            ) : !aiVerdict?.error && (
              <p className="text-xs text-content-tertiary mt-1">
                Ask AI whether this page is impersonating your brand or is a legitimate same-name entity.
              </p>
            )}
          </div>

          {/* App-impersonation details */}
          {threat.threatCategory === "App" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 p-3 bg-red-50 border border-red-100 rounded-lg">
              <div>
                <div className="text-xs text-content-tertiary mb-1">Source</div>
                <div className="text-sm text-content-primary">{threat.appStore === "DomainApk" ? "APK served on domain" : threat.appStore}</div>
              </div>
              {threat.packageName && (
                <div>
                  <div className="text-xs text-content-tertiary mb-1">Package / bundle</div>
                  <div className="text-sm font-mono text-content-primary break-all">{threat.packageName}</div>
                </div>
              )}
              {threat.developerName && (
                <div>
                  <div className="text-xs text-content-tertiary mb-1">Developer</div>
                  <div className="text-sm text-content-primary">{threat.developerName}</div>
                </div>
              )}
              {threat.incidentType && (
                <div>
                  <div className="text-xs text-content-tertiary mb-1">Incident type</div>
                  <div className="text-sm text-content-primary">{threat.incidentType}</div>
                </div>
              )}
              {threat.authenticity && (
                <div>
                  <div className="text-xs text-content-tertiary mb-1">Authenticity</div>
                  <div className="text-sm text-content-primary">{threat.authenticity}</div>
                </div>
              )}
              {threat.impersonationScore > 0 && (
                <div>
                  <div className="text-xs text-content-tertiary mb-1">Impersonation</div>
                  <div className="text-sm text-content-primary">{threat.impersonationScore}%</div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {threat.ipAddress && (
              <div>
                <div className="text-xs text-content-tertiary mb-1">IP Address</div>
                <div className="text-sm font-mono text-content-primary">{threat.ipAddress}</div>
              </div>
            )}
            {threat.registrar && (
              <div>
                <div className="text-xs text-content-tertiary mb-1">Registrar</div>
                <div className="text-sm text-content-primary">{threat.registrar}</div>
              </div>
            )}
            {threat.registrationDate && (
              <div>
                <div className="text-xs text-content-tertiary mb-1">Registered</div>
                <div className="text-sm text-content-primary">
                  {new Date(threat.registrationDate).toLocaleDateString()}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-content-tertiary mb-1">First seen</div>
              <div className="text-sm text-content-primary">{new Date(threat.firstSeen ?? threat.discoveredAt).toLocaleDateString()}</div>
            </div>
            {threat.hostingAsn && (
              <div>
                <div className="text-xs text-content-tertiary mb-1">Hosting ASN</div>
                <div className="text-sm font-mono text-content-primary">{threat.hostingAsn}</div>
              </div>
            )}
            {threat.abuseContact && (
              <div>
                <div className="text-xs text-content-tertiary mb-1">Abuse Contact</div>
                <div className="text-sm text-content-primary">{threat.abuseContact}</div>
              </div>
            )}
          </div>

          {/* Detection provenance — why this alert exists */}
          {threat.scoringFactors?.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-content-tertiary mb-2">
                {aiVerdict?.verdict === "Impersonation" || aiCleared
                  ? `Initial detection signals (detection score ${threat.confidenceScore}/100 — the AI verdict above is authoritative)`
                  : `Why this was flagged — detection score ${threat.confidenceScore}/100`}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {threat.scoringFactors.map(f => (
                  <span
                    key={f.key}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-theme-secondary border border-border rounded text-xs text-content-secondary"
                  >
                    <CheckCircle className="h-3 w-3 text-emerald-600" />
                    {f.label}
                    <span className="text-content-tertiary">+{f.points}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Evidence chain-of-custody — captured at detection, survives site takedown */}
          {threat.hasEvidence && (
            <div className="mb-4 flex items-start gap-2 text-xs text-content-tertiary">
              <Shield className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                Evidence captured {threat.evidenceCapturedAt ? new Date(threat.evidenceCapturedAt).toLocaleString() : "at detection"}
                {threat.evidenceSha256 && (
                  <> · integrity hash <span className="font-mono">{threat.evidenceSha256.slice(0, 12)}…</span></>
                )}
              </div>
            </div>
          )}

          {canManage && (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status dropdown */}
              <div className="relative">
                <button
                  onClick={() => setStatusOpen(o => !o)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg text-sm text-content-secondary hover:bg-theme-secondary transition-colors"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                  Change Status
                </button>
                {statusOpen && (
                  <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[180px]">
                    {statusOptions.map(s => (
                      <button
                        key={s}
                        onClick={() => { onStatusChange(threat.id, s); setStatusOpen(false); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-700"
                      >
                        {STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Takedown button — gated: only High/Critical threats with captured
                  evidence can be escalated, to avoid filing reports on legit third parties */}
              {threat.status !== "Resolved" && threat.status !== "FalsePositive" && (
                takedownAllowed ? (
                  <button
                    onClick={() => onTakedown(threat)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Flag className="h-3.5 w-3.5" />
                    Request Takedown
                  </button>
                ) : (
                  <span className="text-xs text-content-tertiary italic">
                    {aiCleared
                      ? "Takedown unavailable — AI assessed this as a legitimate different entity (re-validate if you disagree)"
                      : "Takedown is available for High and Critical threats only"}
                  </span>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const WEB_FORM_URLS = {
  Google: (domain) => `https://safebrowsing.google.com/safebrowsing/report_phish/?url=https://${domain}`,
  Microsoft: () => `https://www.microsoft.com/en-us/wdsi/support/report-unsafe-site-guest`,
};

function TakedownModal({ threat, onClose, onSubmit, onEnrich }) {
  const apiCall = useApi();
  const [targetType, setTargetType] = useState("Registrar");
  const [contactEmail, setContactEmail] = useState(threat.abuseContact ?? "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enriched, setEnriched] = useState(!!threat.abuseContact);
  const [verified, setVerified] = useState(false);

  const isWebForm = targetType === "Google" || targetType === "Microsoft";
  const noEmailAfterLookup = !isWebForm && enriched && !contactEmail.trim();
  const canSubmit = verified && !enriching && !saving && (isWebForm || !!contactEmail.trim());
  const [enrichAttempts, setEnrichAttempts] = useState(0);

  const doEnrich = useCallback(async () => {
    setEnriching(true);
    try {
      const updated = await apiCall("POST", `/brand-protection/threats/${threat.id}/enrich`);
      if (updated?.abuseContact && !contactEmail) setContactEmail(updated.abuseContact);
      if (onEnrich && updated) onEnrich(updated);
    } catch { /* non-critical — user can enter email manually */ }
    finally {
      setEnriching(false);
      setEnriched(true);
    }
  }, [apiCall, threat.id, contactEmail, onEnrich]);

  // First attempt on mount — enrich if we lack an abuse contact OR captured
  // evidence (the backend requires evidence to accept the takedown).
  useEffect(() => {
    if (!threat.abuseContact || !threat.hasEvidence) doEnrich();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-retry once if first attempt found no email
  useEffect(() => {
    if (enriched && !contactEmail.trim() && enrichAttempts < 1) {
      setEnrichAttempts(a => a + 1);
      const t = setTimeout(() => doEnrich(), 1500);
      return () => clearTimeout(t);
    }
  }, [enriched]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setSaving(true);
    if (isWebForm) window.open(WEB_FORM_URLS[targetType](threat.domain), "_blank", "noopener,noreferrer");
    await onSubmit({ brandThreatId: threat.id, targetType, contactEmail: contactEmail.trim() || null, notes, confirmed: verified });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">Request Takedown</h3>
            <p className="text-sm text-gray-500 mt-0.5 font-mono">{threat.domain}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report To</label>
            <select
              value={targetType}
              onChange={e => setTargetType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="Registrar">Domain Registrar (email)</option>
              <option value="Hosting">Hosting Provider (email)</option>
              <option value="Google">Google Safe Browsing (web form)</option>
              <option value="Microsoft">Microsoft SmartScreen (web form)</option>
            </select>
          </div>

          {/* Web-form targets: show direct link panel */}
          {isWebForm && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-xs text-blue-800 font-medium mb-1">
                {targetType === "Google" ? "Google Safe Browsing" : "Microsoft SmartScreen"} — Web Form
              </p>
              {targetType === "Google" ? (
                <p className="text-xs text-blue-700">
                  Clicking "Log &amp; Open Form" will open the Google phishing report page with this domain
                  pre-filled. Complete the form there to finish the report.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-blue-700">
                    Clicking "Log &amp; Open Form" will open the Microsoft report page. You will need to paste the URL below into the form:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-white border border-blue-200 rounded px-2 py-1.5 text-blue-900 select-all break-all">
                      https://{threat.domain}
                    </code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(`https://${threat.domain}`)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                    >
                      <Copy className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email-based targets */}
          {!isWebForm && (
            <div>
              {/* Email available — hide address, show confirmation only */}
              {contactEmail.trim() && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <p className="text-xs text-green-800 font-medium">Abuse contact found — email will be sent automatically on submit.</p>
                </div>
              )}

              {/* Looking up email */}
              {enriching && !contactEmail.trim() && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50">
                  <Loader className="h-4 w-4 animate-spin text-gray-400 flex-shrink-0" />
                  <p className="text-xs text-gray-500">Looking up registrar abuse contact…</p>
                </div>
              )}

              {/* No email found — input hidden (kept for future use), show alternatives */}
              {!contactEmail.trim() && !enriching && (
                <div>
                  {/* Hidden — kept for future use */}
                  <div style={{display:'none'}}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Abuse Contact Email</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={e => setContactEmail(e.target.value)}
                      placeholder="abuse@registrar.com"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  {noEmailAfterLookup && (
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-2">
                      <p className="text-xs font-medium text-orange-800">No registrar abuse contact found. Use a web-form report instead:</p>
                      <div className="flex gap-2 flex-wrap">
                        <button type="button" onClick={() => setTargetType("Google")}
                          className="px-2.5 py-1 bg-white border border-orange-300 rounded text-xs text-orange-700 hover:bg-orange-100 font-medium">
                          Switch to Google Safe Browsing
                        </button>
                        <button type="button" onClick={() => setTargetType("Microsoft")}
                          className="px-2.5 py-1 bg-white border border-orange-300 rounded text-xs text-orange-700 hover:bg-orange-100 font-medium">
                          Switch to Microsoft SmartScreen
                        </button>
                      </div>
                      <button type="button" onClick={doEnrich} className="text-xs text-orange-600 hover:text-orange-700 font-medium">
                        Retry registrar lookup
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Evidence</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Describe the abuse and evidence..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
            />
          </div>

          {/* Verification disclaimer */}
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Verification Required</p>
                <p className="text-xs text-amber-700 mt-1">
                  By submitting this takedown request you confirm that you have independently verified this
                  domain is a genuine threat and not a false positive. Sending a notice to a legitimate domain
                  may result in unintended disruption.
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={verified}
                onChange={e => setVerified(e.target.checked)}
                className="h-4 w-4 rounded border-amber-400 accent-amber-600"
              />
              <span className="text-xs font-medium text-amber-800">
                I have reviewed this threat and confirm it is a true positive.
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />}
            {isWebForm ? "Log & Open Form" : "Submit Takedown"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BrandProtectionDashboard() {
  const apiCall = useApi();
  const { canAdministerOrganization, canManageOrganizationRisks } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const isAdmin = canAdministerOrganization(org?.organizationId);
  const canManage = canManageOrganizationRisks ? canManageOrganizationRisks(org?.organizationId) : isAdmin;

  const [activeTab, setActiveTab] = useState("threats");
  const [statusFilter, setStatusFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [aiVerdicts, setAiVerdicts] = useState({}); // threatId -> { verdict, confidence, reason, loading }

  const [summary, setSummary] = useState(null);
  const [threats, setThreats] = useState([]);
  const [takedowns, setTakedowns] = useState([]);
  const [config, setConfig] = useState(null);
  const [brands, setBrands] = useState([]);
  const [registeredDomains, setRegisteredDomains] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [manualValue, setManualValue] = useState("");
  const [submittingManual, setSubmittingManual] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [fairSignal, setFairSignal] = useState(null);

  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [error, setError] = useState(null);
  const [takedownTarget, setTakedownTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, threatData, takedownData, configData, campaignData, fairSignalData] = await Promise.all([
        apiCall("GET", "/brand-protection/summary").catch((e) => {
          return null;
        }),
        apiCall("GET", "/brand-protection/threats").catch((e) => {
          return [];
        }),
        apiCall("GET", "/brand-protection/takedowns").catch((e) => {
          return [];
        }),
        apiCall("GET", "/brand-protection/config").catch((e) => {
          return null;
        }),
        apiCall("GET", "/brand-protection/campaigns").catch((e) => {
          return [];
        }),
        apiCall("GET", "/brand-protection/fair-signal").catch((e) => {
          return null;
        }),
      ]);
      setSummary(summaryData);
      setThreats(threatData ?? []);
      // Seed persisted GPT-5.2 verdicts so the tier/badges/sort survive a refresh
      // without re-spending tokens.
      setAiVerdicts(Object.fromEntries(
        (threatData ?? [])
          .filter(t => t.aiVerdict)
          .map(t => [t.id, { verdict: t.aiVerdict, confidence: t.aiVerdictConfidence, reason: t.aiVerdictReason }])
      ));
      setTakedowns(takedownData ?? []);
      setConfig(configData);
      setBrands(configData?.brands ?? []);
      setRegisteredDomains(configData?.registeredDomains ?? []);
      setAllowlist(configData?.allowlist ?? []);
      setCampaigns(campaignData ?? []);
      setFairSignal(fairSignalData ?? null);
    } catch (e) {
      setError("Failed to load brand protection data.");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const preScanTime = config?.lastScannedAt ?? null;
      // Runs every scanner for this org (domains, apps, urlscan, phish feeds, CT logs).
      // DNSTwist updates lastScannedAt, which we poll below as the completion signal.
      await apiCall("POST", "/brand-protection/scan-all");

      // Poll every 6s until lastScannedAt changes (scan finished) or 4min timeout
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const latest = await apiCall("GET", "/brand-protection/config");
          const done = latest?.lastScannedAt && latest.lastScannedAt !== preScanTime;
          const timedOut = Date.now() - started > 240000;
          if (done || timedOut) {
            clearInterval(poll);
            setScanning(false);
            await fetchAll(); // refresh threats + summary
          }
        } catch { /* keep polling */ }
      }, 6000);
    } catch (e) {
      const msg = e?.response?.status === 429
        ? (e.response?.data?.message || "A scan was triggered recently. Try again in a few minutes.")
        : "Failed to trigger scan.";
      setError(msg);
      setScanning(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await apiCall("PATCH", `/brand-protection/threats/${id}/status`, { status });
      setThreats(prev => prev.map(t => t.id === id ? { ...t, status } : t));

      // False-positive feedback loop: offer to allowlist the domain so it — and
      // its siblings — never re-alert. Applying it saves config immediately.
      const suggestion = res?.allowlistSuggestion;
      if (suggestion && !allowlist.includes(suggestion)) {
        if (window.confirm(
          `Marked as false positive.\n\nAdd "${suggestion}" to the allowlist so it (and its subdomains) are never flagged again?`
        )) {
          const nextAllowlist = [...allowlist, suggestion];
          setAllowlist(nextAllowlist);
          const saved = await apiCall("POST", "/brand-protection/config", {
            brands, registeredDomains, allowlist: nextAllowlist,
            isEnabled: config?.isEnabled ?? true,
            scanFrequencyHours: config?.scanFrequencyHours ?? 24,
          });
          setConfig(saved);
        }
      }
    } catch {
      setError("Failed to update status.");
    }
  };

  const handleTakedownSubmit = async (dto) => {
    try {
      const result = await apiCall("POST", "/brand-protection/takedowns", dto);
      setTakedowns(prev => [result, ...prev]);
      setThreats(prev => prev.map(t => t.id === dto.brandThreatId ? { ...t, status: "TakedownRequested" } : t));
    } catch (e) {
      // The backend gate rejects unconfirmed / low-severity / no-evidence takedowns
      setError(e?.response?.data?.message ?? "Failed to submit takedown request.");
    }
  };

  const handleManualSubmit = async () => {
    const value = manualValue.trim();
    if (!value) return;
    setSubmittingManual(true);
    try {
      const created = await apiCall("POST", "/brand-protection/submit", { value });
      setThreats(prev => [created, ...prev.filter(t => t.id !== created.id)]);
      setManualValue("");
      setActiveTab("threats");
    } catch (e) {
      setError(e?.response?.data?.message ?? "Failed to submit URL.");
    } finally {
      setSubmittingManual(false);
    }
  };

  const handleThreatEnrich = (updatedThreat) => {
    setThreats(prev => prev.map(t => t.id === updatedThreat.id ? { ...t, ...updatedThreat } : t));
  };

  const handleAiValidate = async (threat, force = false) => {
    setAiVerdicts(prev => ({ ...prev, [threat.id]: { ...prev[threat.id], loading: true, error: null } }));
    try {
      const res = await apiCall("POST", `/brand-protection/ai/threats/${threat.id}/validate${force ? "?force=true" : ""}`);
      setAiVerdicts(prev => ({ ...prev, [threat.id]: { ...res, loading: false } }));
    } catch (e) {
      // A failed call (429 cooldown, network, server error) is NOT a verdict —
      // keep any previous real verdict and surface the failure separately, so a
      // rate-limit message can't masquerade as an "Uncertain" AI assessment.
      const fallback = e?.response?.status === 429
        ? "AI validation is rate-limited — please wait a moment and retry."
        : "AI validation failed.";
      setAiVerdicts(prev => ({
        ...prev,
        [threat.id]: { ...prev[threat.id], loading: false, error: e?.response?.data?.message ?? fallback },
      }));
    }
  };

  const handleSaveConfig = async () => {
    // Removing a monitored term (token or registered domain) cascade-deletes
    // everything it produced, so confirm first. Allowlist changes don't cascade.
    const prevMonitored = [...(config?.brands ?? []), ...(config?.registeredDomains ?? [])];
    const newMonitored = [...brands, ...registeredDomains];
    const removed = prevMonitored.filter(b => !newMonitored.includes(b));
    const added = newMonitored.filter(b => !prevMonitored.includes(b));

    if (removed.length > 0) {
      const list = removed.map(b => `"${b}"`).join(", ");
      const noun = removed.length === 1 ? "monitored term" : "monitored terms";
      const msg =
        `You have removed ${removed.length} ${noun} (${list}). ` +
        `This will permanently delete all detected threats for ${removed.length === 1 ? "it" : "them"} — ` +
        `including their takedown requests and any associated risks and remediations. Do you want to continue?`;
      if (!window.confirm(msg)) return;
    }

    setSavingConfig(true);
    try {
      const saved = await apiCall("POST", "/brand-protection/config", {
        brands,
        registeredDomains,
        allowlist,
        isEnabled: config?.isEnabled ?? true,
        scanFrequencyHours: config?.scanFrequencyHours ?? 24,
      });
      setConfig(saved);
      // Newly added terms aren't scanned until the daily background cycle — kick off a
      // scan now so results appear. handleScan() polls and auto-refreshes (same as "Scan Now").
      if (added.length > 0) {
        handleScan();
      } else if (removed.length > 0) {
        // Removal cascade-deletes threats synchronously server-side, so refresh the
        // threats/summary/takedowns view to drop the now-deleted rows.
        await fetchAll();
      }
    } catch {
      setError("Failed to save configuration.");
    } finally {
      setSavingConfig(false);
    }
  };

  // Filtered threats
  // All filters EXCEPT tier — so the tier toggle counts reflect the other active
  // filters (e.g. with Risk=Critical selected, "Confirmed (N)" matches the list).
  const passesNonTierFilters = t => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (riskFilter !== "all" && effectiveRiskLevel(t, aiVerdicts[t.id]) !== riskFilter) return false;
    if (typeFilter === "App") { if (t.threatCategory !== "App") return false; }
    else if (typeFilter !== "all" && t.threatType !== typeFilter) return false;
    if (searchQuery && !t.domain.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !t.brandTerm.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  };
  const tierScoped = threats.filter(passesNonTierFilters);
  const filteredThreats = tierScoped.filter(t => tierFilter === "all" || threatTier(t, aiVerdicts[t.id]) === tierFilter);

  // Float the alarming ones to the top: AI-confirmed fakes first, then deterministic
  // Confirmed, then Possible, and AI-cleared "legitimate" rows sink to the bottom.
  const SEV_RANK = { Critical: 3, High: 2, Medium: 1, Low: 0 };
  const rowRank = t => {
    const av = aiVerdicts[t.id];
    if (av?.verdict === "Impersonation") return 0;
    // Rank off the tier, not the raw verdict: a Web-Risk-confirmed threat must
    // never sink to the bottom on an advisory AI "legitimate" call.
    const tier = threatTier(t, av);
    if (tier === "LikelyLegitimate") return 4;
    return tier === "Confirmed" ? 1 : 2;
  };
  const sortedThreats = [...filteredThreats].sort((a, b) =>
    rowRank(a) - rowRank(b) ||
    (SEV_RANK[effectiveRiskLevel(b, aiVerdicts[b.id])] ?? 0) - (SEV_RANK[effectiveRiskLevel(a, aiVerdicts[a.id])] ?? 0));

  const statusTabs = [
    { key: "all", label: "All", count: threats.length },
    { key: "New", label: "New", count: threats.filter(t => t.status === "New").length },
    { key: "UnderReview", label: "Under Review", count: threats.filter(t => t.status === "UnderReview").length },
    { key: "TakedownRequested", label: "TD Requested", count: threats.filter(t => t.status === "TakedownRequested").length },
    { key: "Resolved", label: "Resolved", count: threats.filter(t => t.status === "Resolved").length },
  ];

  if (loading) return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="flex justify-center py-16"><Loader className="h-7 w-7 animate-spin text-orange-600" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      {takedownTarget && (
        <TakedownModal
          threat={takedownTarget}
          onClose={() => setTakedownTarget(null)}
          onSubmit={handleTakedownSubmit}
          onEnrich={handleThreatEnrich}
        />
      )}

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-100 rounded-xl">
              <Shield className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-content-primary">Brand Protection</h1>
              <p className="text-sm text-content-secondary mt-0.5">
                Monitor and respond to brand abuse, typosquatting, and lookalike domains.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config?.lastScannedAt && (
              <span className="text-xs text-content-tertiary">
                Last scan: {timeAgo(config.lastScannedAt)}
              </span>
            )}
            {config?.isEnabled && (
              <span
                className={`text-xs ${config?.lastCertStreamPollAt ? "text-content-tertiary" : "text-orange-600"}`}
                title="Certificate-log monitoring runs continuously in the background; this is its last successful data pull"
              >
                · Cert monitor: {config?.lastCertStreamPollAt ? timeAgo(config.lastCertStreamPollAt) : "no successful poll yet"}
              </span>
            )}
            {canManage && (
              <button
                onClick={handleScan}
                disabled={scanning}
                title="Run all brand scanners now for this organization: domain look-alikes, impersonating apps, urlscan content, phishing feeds, and certificate logs"
                className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm text-content-secondary hover:bg-theme-secondary transition-colors disabled:opacity-50"
              >
                {scanning ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Scan Now
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="Total Active" value={summary.totalThreats} color="gray" />
            <StatCard label="Critical" value={summary.critical} color="red" />
            <StatCard label="High Risk" value={summary.high} color="orange" />
            <StatCard label="Takedown Requested" value={summary.takedownRequested} color="yellow" />
          </div>
        )}

        {/* No monitored terms configured */}
        {(!config || (!config.brands?.length && !config.registeredDomains?.length)) && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium text-orange-800">No monitoring configured</div>
              <div className="text-sm text-orange-700 mt-0.5">
                Go to the <button onClick={() => setActiveTab("settings")} className="underline font-medium">Settings tab</button> to add your registered domains and brand tokens.
              </div>
            </div>
          </div>
        )}

        {/* Main tabs */}
        <div className="border-b border-border mb-6">
          <nav className="-mb-px flex gap-6">
            {[
              { key: "threats", label: "Threats", icon: AlertTriangle, count: threats.length },
              { key: "campaigns", label: "Campaigns", icon: Globe, count: campaigns.length },
              { key: "takedowns", label: "Takedowns", icon: FileText, count: takedowns.length },
              { key: "settings", label: "Settings", icon: Settings },
            ].map(({ key, label, icon: Icon, count }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === key
                    ? "border-orange-600 text-orange-600"
                    : "border-transparent text-content-secondary hover:text-content-primary"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
                {count != null && count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === key ? "bg-orange-100 text-orange-700" : "bg-theme-secondary text-content-secondary"
                  }`}>{count}</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Threats tab ── */}
        {activeTab === "threats" && (
          <div>
            {/* Manual submission — report a suspicious URL or domain into the pipeline */}
            {canManage && (
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <FileText className="absolute left-2.5 top-2.5 h-4 w-4 text-content-tertiary" />
                  <input
                    value={manualValue}
                    onChange={e => setManualValue(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleManualSubmit()}
                    placeholder="Report a suspicious URL or domain (e.g. abc-login.example.com)"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-theme-secondary text-content-primary text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <button
                  onClick={handleManualSubmit}
                  disabled={submittingManual || !manualValue.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {submittingManual ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Submit
                </button>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              {/* Status filter */}
              <div className="flex gap-1 bg-theme-secondary border border-border rounded-lg p-1">
                {statusTabs.map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(key)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      statusFilter === key
                        ? "bg-white shadow text-content-primary"
                        : "text-content-secondary hover:text-content-primary"
                    }`}
                  >
                    {label}
                    {count > 0 && <span className="ml-1 opacity-60">({count})</span>}
                  </button>
                ))}
              </div>

              {/* Tier 2 precision toggle: confirmed impersonation vs AI-cleared vs name-collision noise */}
              <div className="inline-flex rounded-lg border border-border overflow-hidden">
                {[
                  { key: "Confirmed", label: "Likely Impersonation (AI)" },
                  { key: "LikelyLegitimate", label: "Likely Legitimate (AI)" },
                  { key: "Possible", label: "Possible" },
                  { key: "all", label: "All" },
                ].map(({ key, label }) => {
                  const count = key === "all"
                    ? tierScoped.length
                    : tierScoped.filter(t => threatTier(t, aiVerdicts[t.id]) === key).length;
                  const active = tierFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setTierFilter(key)}
                      title={key === "Confirmed"
                        ? "Hard-evidence or AI-confirmed impersonation: logo clone, cert mismatch, confirmed phishing, typosquat, or AI verdict"
                        : key === "LikelyLegitimate"
                          ? "AI confirmed this is a legitimate same-name entity, not an impersonation — severity downgraded to Low"
                          : key === "Possible"
                            ? "Name/title mention only — ambiguous; needs review (validate with AI to resolve)"
                            : "All findings"}
                      className={`text-sm px-3 py-1.5 ${active
                        ? "bg-orange-500 text-white"
                        : "bg-theme-secondary text-content-secondary hover:text-content-primary"}`}
                    >
                      {label} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Risk filter */}
              <select
                value={riskFilter}
                onChange={e => setRiskFilter(e.target.value)}
                className="rounded-lg border border-border bg-theme-secondary text-content-secondary text-sm px-3 py-1.5 focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Risk Levels</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>

              {/* Source/Type filter */}
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="rounded-lg border border-border bg-theme-secondary text-content-secondary text-sm px-3 py-1.5 focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Sources ({threats.length})</option>
                <option value="DNSTwist">DNSTwist ({threats.filter(t => t.threatType === "DNSTwist").length})</option>
                <option value="CertStream">CertStream ({threats.filter(t => t.threatType === "CertStream").length})</option>
                <option value="PhishFeed">Phishing Feeds ({threats.filter(t => t.threatType === "PhishFeed").length})</option>
                <option value="UrlScan">Content match ({threats.filter(t => t.threatType === "UrlScan").length})</option>
                <option value="Manual">Manual ({threats.filter(t => t.threatType === "Manual").length})</option>
                <option value="App">App impersonation ({threats.filter(t => t.threatCategory === "App").length})</option>
              </select>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-4 w-4 text-content-tertiary" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search domains..."
                  className="pl-8 pr-3 py-1.5 rounded-lg border border-border bg-theme-secondary text-sm text-content-primary placeholder-content-tertiary focus:ring-2 focus:ring-orange-500 w-48"
                />
              </div>
            </div>

            {filteredThreats.length === 0 ? (
              <div className="text-center py-16 text-content-secondary">
                <Shield className="h-12 w-12 mx-auto mb-3 text-green-400 opacity-50" />
                <div className="font-medium">No threats found</div>
                <div className="text-sm mt-1 text-content-tertiary">
                  {threats.length === 0
                    ? "Run a scan to detect lookalike domains."
                    : "No threats match the current filters."}
                </div>
              </div>
            ) : (
              <div>
                <div className="text-xs text-content-tertiary mb-3">
                  Showing {filteredThreats.length} of {threats.length} threats
                </div>
                {sortedThreats.map(t => (
                  <ThreatRow
                    key={t.id}
                    threat={t}
                    onStatusChange={handleStatusChange}
                    onTakedown={setTakedownTarget}
                    canManage={canManage}
                    aiVerdict={aiVerdicts[t.id]}
                    onAiValidate={handleAiValidate}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Campaigns tab ── */}
        {activeTab === "campaigns" && (
          <div>
            <p className="text-sm text-content-secondary mb-4">
              Threats grouped by shared infrastructure (IP, hosting network, registrar, registration bursts).
              Each campaign is likely one actor operating multiple lookalike domains — the unit a takedown and a
              financial-exposure estimate should target.
            </p>

            {/* FAIR frequency signal — advisory, not yet wired into the live financial engine */}
            {fairSignal && fairSignal.tefMultiplier > 1 && (
              <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-amber-900">
                        Elevated phishing / credential-theft frequency: {fairSignal.tefMultiplier}×
                      </span>
                      <span className="text-xs text-amber-700">
                        from {fairSignal.eventCount} active threat event{fairSignal.eventCount === 1 ? "" : "s"}
                      </span>
                    </div>
                    <p className="text-xs text-amber-800 mt-1">
                      Confirmed brand-abuse activity raises the modeled likelihood of a successful phishing
                      attack. This advisory signal will feed your Financial Exposure model once FAIR
                      integration is enabled.
                    </p>
                    {fairSignal.drivers?.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {fairSignal.drivers.map((d, i) => (
                          <li key={i} className="text-xs text-amber-700 font-mono">• {d}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            {campaigns.length === 0 ? (
              <div className="text-center py-16 text-content-secondary">
                <Globe className="h-10 w-10 text-content-tertiary mx-auto mb-3" />
                No multi-domain campaigns detected. Threats sharing infrastructure will be grouped here.
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map(c => {
                  const rs = RISK_STYLE[c.maxSeverity] ?? RISK_STYLE.Medium;
                  return (
                    <div key={c.clusterKey} className="border border-border rounded-xl p-4 bg-theme-secondary">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${rs.dot}`} />
                            <span className="font-semibold text-content-primary">
                              Campaign · {c.memberCount} domains
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${rs.badge}`}>
                              {c.maxSeverity}
                            </span>
                            {c.maxConfidence > 0 && (
                              <span className="text-xs text-content-tertiary">{c.maxConfidence}% max confidence</span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {c.sharedSignals.map((s, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-theme-primary border border-border rounded text-xs text-content-secondary">
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-xs text-content-tertiary text-right">
                          First seen {new Date(c.firstSeen).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {c.domains.map(d => (
                          <span key={d} className="font-mono text-xs px-2 py-1 bg-theme-primary border border-border rounded">
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Takedowns tab ── */}
        {activeTab === "takedowns" && (
          <div>
            {takedowns.length === 0 ? (
              <div className="text-center py-16 text-content-secondary">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <div className="font-medium">No takedown requests</div>
                <div className="text-sm mt-1 text-content-tertiary">
                  Takedown requests will appear here once submitted from the Threats tab.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {takedowns.map(r => (
                  <div key={r.id} className="bg-theme-secondary border border-border rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-content-primary">Takedown #{r.id}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TAKEDOWN_STATUS_STYLE[r.status] ?? ""}`}>
                            {TAKEDOWN_STATUS_LABEL[r.status] ?? r.status}
                          </span>
                          <span className="text-xs text-content-tertiary">Target: {r.targetType}</span>
                        </div>
                        {r.contactEmail && (
                          <div className="text-xs text-content-tertiary mt-0.5">{r.contactEmail}</div>
                        )}
                        {r.notes && (
                          <div className="text-xs text-content-secondary mt-1 line-clamp-2">{r.notes}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-content-tertiary">{timeAgo(r.createdAt)}</div>
                        {r.responseDeadline && (
                          <div className={`text-xs mt-0.5 ${new Date(r.responseDeadline) < new Date() ? "text-red-600" : "text-content-tertiary"}`}>
                            <Clock className="h-3 w-3 inline mr-1" />
                            Deadline: {new Date(r.responseDeadline).toLocaleDateString()}
                          </div>
                        )}
                        {r.escalationCount > 0 && (
                          <div className="text-xs text-orange-600 mt-0.5">
                            Escalated {r.escalationCount}×
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Settings tab ── */}
        {activeTab === "settings" && (
          <div className="max-w-2xl space-y-5">
            {/* Structured brand configuration */}
            <TermListEditor
              icon={<Globe className="h-5 w-5 text-orange-600" />}
              title="Registered Domains"
              description="Your organization's actual domains (e.g. axaris.ai). The scanner hunts for lookalike variations — misspellings, swapped letters, homoglyphs — of these. They are automatically allowlisted so your own sites are never flagged."
              placeholder="e.g. axaris.ai"
              items={registeredDomains}
              onChange={setRegisteredDomains}
              canManage={canManage}
              emptyText="No registered domains yet — add your organization's real domains to enable lookalike scanning."
            />
            <TermListEditor
              icon={<Shield className="h-5 w-5 text-orange-600" />}
              title="Brand Tokens"
              description="Distinctive brand words watched for inside any new certificate or hostname (e.g. axaris matches axaris-login.pages.dev). Generic words like 'bank' or 'secure' are ignored to prevent false positives."
              placeholder="e.g. axaris"
              items={brands}
              onChange={setBrands}
              canManage={canManage}
              emptyText="No brand tokens yet."
            />
            <TermListEditor
              icon={<CheckCircle className="h-5 w-5 text-emerald-600" />}
              title="Allowlist"
              description="Domains that must never be flagged: legitimate third parties that happen to share your brand string (name collisions). Matching includes their subdomains; lookalikes embedding an allowlisted name inside another domain are still flagged."
              placeholder="e.g. a-legitimate-company.com"
              items={allowlist}
              onChange={setAllowlist}
              canManage={canManage}
              emptyText="No allowlist entries."
            />

            {/* Official mobile apps (fake-app registry) */}
            <OfficialAppsPanel apiCall={apiCall} canManage={canManage} />

            {/* Scan frequency */}
            <div className="bg-theme-secondary border border-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-5 w-5 text-orange-600" />
                <h2 className="text-base font-semibold text-content-primary">Scan Settings</h2>
              </div>
              <p className="text-xs text-content-tertiary mt-1">
                Controls how often the periodic scanners run — domain look-alikes, impersonating apps,
                and urlscan content. Certificate-log and phishing-feed monitoring run continuously in
                the background regardless of this setting.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <label className="text-sm text-content-secondary">Scan every</label>
                <select
                  value={config?.scanFrequencyHours ?? 24}
                  onChange={e => setConfig(c => ({ ...c, scanFrequencyHours: parseInt(e.target.value) }))}
                  disabled={!canManage}
                  className="rounded-lg border border-border bg-theme-primary text-content-primary text-sm px-3 py-2 focus:ring-2 focus:ring-orange-500"
                >
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>48 hours</option>
                  <option value={168}>1 week</option>
                </select>

                <label className="flex items-center gap-2 text-sm text-content-secondary ml-4">
                  <input
                    type="checkbox"
                    checked={config?.isEnabled ?? true}
                    onChange={e => setConfig(c => ({ ...c, isEnabled: e.target.checked }))}
                    disabled={!canManage}
                    className="rounded border-gray-300 text-orange-600"
                  />
                  Enable automatic scanning
                </label>
              </div>
            </div>

            {canManage && (
              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {savingConfig ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Save Configuration
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
